/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createPortal,
} from 'preact/compat';
import {
  Container,
  Slot,
  SlotProps,
} from '@adobe-commerce/elsie/lib';
import { formattedDate } from '@/quote-management/utils/dateUtils';
import {
  NegotiableQuoteModel,
  CartItemModel,
} from '@/quote-management/data/models/negotiable-quote-model';
import {
  ManageNegotiableQuote as ManageNegotiableQuoteComponent,
  ManageNegotiableQuoteProps as ManageNegotiableQuoteComponentProps,
} from '@/quote-management/components/ManageNegotiableQuote';
import { events } from '@adobe-commerce/event-bus';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  ActionsBar,
  TabbedContent,
  ConfirmationModal,
  AttachedFilesList,
  AttachedFile,
} from '@/quote-management/components';
import { ShippingAddressDisplay } from '@/quote-management/containers/ShippingAddressDisplay';
import { QuoteHistoryLog } from '@/quote-management/containers/QuoteHistoryLog';
import { ItemsQuoted, QuoteCommentsList } from '@/quote-management/containers';
import {
  Button,
  InLineAlert,
  TextArea,
  InputFile,
} from '@adobe-commerce/elsie/components';
import {
  WarningFilled,
  Edit,
  CheckWithCircle,
  Add,
} from '@adobe-commerce/elsie/icons';
import {
  deleteQuote,
  sendForReview,
  renameNegotiableQuote,
  closeNegotiableQuote,
  getQuoteData,
  uploadFile,
  duplicateQuote,
  createQuoteTemplate,
} from '@/quote-management/api';
import {
  formatFileSize,
  validateFileType,
  validateFileSize,
} from '@/quote-management/utils/fileUtils';
import { RenameQuoteModal } from '@/quote-management/components/RenameQuoteModal';

export interface ManageNegotiableQuoteProps
  extends HTMLAttributes<HTMLDivElement> {
  onActionsDropdownChange?: (event: Event) => void;
  onActionsButtonClick?: (action: string) => void;
  onSendForReview?: (params: {
    quoteData: NegotiableQuoteModel;
    comment?: string;
    attachments?: AttachedFile[];
  }) => void;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  onDuplicateQuote?: (params: {
    quoteData: NegotiableQuoteModel;
    newQuote: NegotiableQuoteModel;
  }) => void;
  slots?: {
    QuoteName?: SlotProps<{
      quoteName?: string;
      quoteData?: NegotiableQuoteModel;
    }>;
    QuoteStatus?: SlotProps<{
      quoteStatus?: string;
      quoteData?: NegotiableQuoteModel;
    }>;
    Banner?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    DuplicateQuoteWarningBanner?: SlotProps<{
      outOfStockWarning?: boolean;
    }>;
    Details?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    ActionBar?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
      actionsBarDropdownValue?: string;
    }>;
    QuoteContent?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    ItemsQuotedTab?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    CommentsTab?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    HistoryLogTab?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    ShippingInformationTitle?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    ShippingInformation?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
      loading?: boolean;
      setLoading?: (loading: boolean) => void;
    }>;
    QuoteCommentsTitle?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    QuoteComments?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
    }>;
    AttachFilesField?: SlotProps<{
      onFileChange: (files: File[]) => void;
      attachedFiles: AttachedFile[];
      fileUploadError: string | undefined;
      disabled?: boolean;
    }>;
    AttachedFilesList?: SlotProps<{
      files: AttachedFile[];
      onRemove: (key: string) => void;
      disabled?: boolean;
    }>;
    Footer?: SlotProps<{
      quoteData?: NegotiableQuoteModel;
      comment?: string;
      isSubmitting?: boolean;
      attachments?: AttachedFile[];
      handleSendForReview: () => void;
    }>;
  };
}

export const ManageNegotiableQuote: Container<ManageNegotiableQuoteProps> = ({
  onActionsDropdownChange,
  slots,
  onActionsButtonClick,
  onSendForReview,
  maxFiles,
  maxFileSize,
  acceptedFileTypes,
  onDuplicateQuote,
  ...props
}) => {
  const [quoteData, setQuoteData] = useState<NegotiableQuoteModel | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState<boolean>(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [duplicateSuccess, setDuplicateSuccess] = useState<boolean>(false);
  const [duplicateOutOfStockWarning, setDuplicateOutOfStockWarning] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [modalSuccessMessage, setModalSuccessMessage] = useState<string>('');
  const [modalErrorMessage, setModalErrorMessage] = useState<string>('');
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState<boolean>(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState<boolean>(false);
  const [createTemplateSuccessMessage, setCreateTemplateSuccessMessage] = useState<string>('');
  const [createTemplateErrorMessage, setCreateTemplateErrorMessage] = useState<string>('');
  const [isRenameModalOpen, setIsRenameModalOpen] = useState<boolean>(false);
  const [renameQuoteName, setRenameQuoteName] = useState<string>('');
  const [renameReason, setRenameReason] = useState<string>('');
  const [renameError, setRenameError] = useState<string>('');
  const [renameQuoteNameError, setRenameQuoteNameError] = useState<string>('');
  const [renameSuccess, setRenameSuccess] = useState<string>('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [fileUploadError, setFileUploadError] = useState<string | undefined>(undefined);
  const [selectedItemUids, setSelectedItemUids] = useState<Set<string>>(
    new Set()
  );
  const selectedItemUidsRef = useRef<Set<string>>(selectedItemUids);
  const removeItemsHandlerRef = useRef<
    ((items: CartItemModel[]) => void) | null
  >(null);
  const [actionsBarDropdownValue, setActionsBarDropdownValue] =
    useState<string>('');
  const actionsBarDropdownValueRef = useRef<string>(actionsBarDropdownValue);
  const [isNoItemsSelectedModalOpen, setIsNoItemsSelectedModalOpen] =
    useState<boolean>(false);

  // Keep refs in sync with state
  useEffect(() => {
    selectedItemUidsRef.current = selectedItemUids;
  }, [selectedItemUids]);

  useEffect(() => {
    actionsBarDropdownValueRef.current = actionsBarDropdownValue;
  }, [actionsBarDropdownValue]);

  const dictionary = useText({
    createdLabel: 'NegotiableQuote.Manage.createdLabel',
    salesRepLabel: 'NegotiableQuote.Manage.salesRepLabel',
    expiresLabel: 'NegotiableQuote.Manage.expiresLabel',
    actionsLabel: 'NegotiableQuote.Manage.actionsLabel',
    remove: 'NegotiableQuote.Manage.actions.remove',
    close: 'NegotiableQuote.Manage.actionButtons.close',
    delete: 'NegotiableQuote.Manage.actionButtons.delete',
    print: 'NegotiableQuote.Manage.actionButtons.print',
    createTemplate: 'NegotiableQuote.Manage.actionButtons.createTemplate',
    createCopy: 'NegotiableQuote.Manage.actionButtons.createCopy',
    shippingInformationTitle:
      'NegotiableQuote.Manage.shippingInformation.title',
    quoteCommentsTitle: 'NegotiableQuote.Manage.quoteComments.title',
    quoteCommentsPlaceholder:
      'NegotiableQuote.Manage.quoteComments.placeholder',
    sendForReview: 'NegotiableQuote.Manage.actionButtons.sendForReview',
    bannerTitle: 'NegotiableQuote.Manage.bannerTitle',
    statusSubmitted: 'NegotiableQuote.Manage.bannerStatusMessages.submitted',
    statusPending: 'NegotiableQuote.Manage.bannerStatusMessages.pending',
    statusExpired: 'NegotiableQuote.Manage.bannerStatusMessages.expired',
    errorFallback:
      'NegotiableQuote.Manage.confirmationModal.delete.errorFallback',
    closeSuccessMessage:
      'NegotiableQuote.Manage.confirmationModal.close.successDescription',
    closeModalTitle: 'NegotiableQuote.Manage.actionButtons.close',
    closeModalMessage: 'NegotiableQuote.Manage.confirmationModal.close.message',
    closeModalCancel: 'NegotiableQuote.Manage.confirmationModal.cancel',
    closeModalConfirm: 'NegotiableQuote.Manage.confirmationModal.close.confirm',
    closeModalConfirmLoading:
      'NegotiableQuote.Manage.confirmationModal.close.confirmLoading',
    closeModalSuccessHeading:
      'NegotiableQuote.Manage.confirmationModal.close.successHeading',
    createTemplateModalTitle: 'NegotiableQuote.Manage.actionButtons.createTemplate',
    createTemplateModalMessage: 'NegotiableQuote.Manage.confirmationModal.createTemplate.message',
    createTemplateModalCancel: 'NegotiableQuote.Manage.confirmationModal.cancel',
    createTemplateModalConfirm: 'NegotiableQuote.Manage.confirmationModal.createTemplate.confirm',
    createTemplateModalConfirmLoading:
      'NegotiableQuote.Manage.confirmationModal.createTemplate.confirmLoading',
    createTemplateModalSuccessHeading:
      'NegotiableQuote.Manage.confirmationModal.createTemplate.successHeading',
    createTemplateSuccessMessage:
      'NegotiableQuote.Manage.confirmationModal.createTemplate.successDescription',
    createTemplateModalErrorHeading:
      'NegotiableQuote.Manage.confirmationModal.createTemplate.errorHeading',
    createTemplateErrorFallback:
      'NegotiableQuote.Manage.confirmationModal.createTemplate.errorFallback',
    renameErrorHeading: 'NegotiableQuote.Manage.rename.errorHeading',
    renameQuoteNameRequired: 'NegotiableQuote.Manage.rename.quoteNameRequired',
    renameErrorDefault: 'NegotiableQuote.Manage.rename.errorDefault',
    renameSuccessHeading: 'NegotiableQuote.Manage.rename.successHeading',
    renameSuccessMessage: 'NegotiableQuote.Manage.rename.successMessage',
    confirmationModalCancel: 'NegotiableQuote.Manage.confirmationModal.cancel',
    confirmationModalDeleteTitle: 'NegotiableQuote.Manage.confirmationModal.delete.title',
    confirmationModalDeleteMessage: 'NegotiableQuote.Manage.confirmationModal.delete.message',
    confirmationModalDeleteConfirm: 'NegotiableQuote.Manage.confirmationModal.delete.confirm',
    confirmationModalDeleteErrorHeading: 'NegotiableQuote.Manage.confirmationModal.delete.errorHeading',
    confirmationModalDeleteErrorFallback: 'NegotiableQuote.Manage.confirmationModal.delete.errorFallback',
    confirmationModalDeleteSuccessHeading: 'NegotiableQuote.Manage.confirmationModal.delete.successHeading',
    confirmationModalDeleteSuccessDescription: 'NegotiableQuote.Manage.confirmationModal.delete.successDescription',
    confirmationModalDuplicateTitle: 'NegotiableQuote.Manage.confirmationModal.duplicate.title',
    confirmationModalDuplicateMessage: 'NegotiableQuote.Manage.confirmationModal.duplicate.message',
    confirmationModalDuplicateConfirm: 'NegotiableQuote.Manage.confirmationModal.duplicate.confirm',
    confirmationModalDuplicateErrorHeading: 'NegotiableQuote.Manage.confirmationModal.duplicate.errorHeading',
    confirmationModalDuplicateErrorFallback: 'NegotiableQuote.Manage.confirmationModal.duplicate.errorFallback',
    confirmationModalDuplicateSuccessHeading: 'NegotiableQuote.Manage.confirmationModal.duplicate.successHeading',
    confirmationModalDuplicateSuccessDescription: 'NegotiableQuote.Manage.confirmationModal.duplicate.successDescription',
    duplicateOutOfStockWarningHeading: 'NegotiableQuote.Manage.confirmationModal.duplicate.outOfStockWarningHeading',
    duplicateOutOfStockWarningMessage: 'NegotiableQuote.Manage.confirmationModal.duplicate.outOfStockWarningMessage',
    noItemsSelectedModalTitle: 'NegotiableQuote.Manage.confirmationModal.noItemsSelected.title',
    noItemsSelectedModalMessage: 'NegotiableQuote.Manage.confirmationModal.noItemsSelected.message',
    noItemsSelectedModalConfirm: 'NegotiableQuote.Manage.confirmationModal.noItemsSelected.confirm',
    attachFiles: 'NegotiableQuote.Manage.attachFiles',
    fileUploadError: 'NegotiableQuote.Manage.fileUploadError',
    maxFilesExceeded: 'NegotiableQuote.Manage.maxFilesExceeded',
    maxFileSizeExceeded: 'NegotiableQuote.Manage.maxFileSizeExceeded',
    invalidFileType: 'NegotiableQuote.Manage.invalidFileType',
  });
  /* Event listeners */
  useEffect(() => {
    const quoteDataEvent = events.on(
      'quote-management/quote-data',
      (data: { quote: NegotiableQuoteModel }) => {
        const { quote } = data;
        if (quote) {
          setQuoteData(quote);
          setLoading(false);

          // Check for pending out of stock warning from duplication
          const pendingWarningKey = `duplicate-outOfStock-warning-${quote.uid}`;
          const pendingWarning = sessionStorage.getItem(pendingWarningKey);
          if (pendingWarning === 'true') {
            setDuplicateOutOfStockWarning(true);
            sessionStorage.removeItem(pendingWarningKey);
          }
        }
      },
      {
        eager: true,
      }
    );
    return () => quoteDataEvent?.off();
  }, []);

  // Listen for shipping address set event
  useEffect(() => {
    const shippingAddressSetEvent = events.on(
      'quote-management/shipping-address-set',
      (data: { quote: NegotiableQuoteModel }) => {
        const { quote } = data;
        if (quote) {
          setQuoteData(quote);
        }
      },
      {
        eager: true,
      }
    );
    return () => shippingAddressSetEvent?.off();
  }, []);

  // Listen for quote sent for review event
  useEffect(() => {
    const quoteSentForReviewEvent = events.on(
      'quote-management/quote-sent-for-review',
      (data: { quote: NegotiableQuoteModel }) => {
        const { quote } = data;
        setQuoteData(quote);
        setComment(''); // Reset comment after successful submission
        setAttachedFiles([]); // Clear attachments after successful submission
        setIsSubmitting(false);
      },
      {
        eager: true,
      }
    );
    return () => quoteSentForReviewEvent?.off();
  }, []);

  // Listen for quote items removed event
  useEffect(() => {
    const quoteItemsRemovedEvent = events.on(
      'quote-management/quote-items-removed',
      (data: { quote: NegotiableQuoteModel; removedItemUids: string[] }) => {
        const { quote, removedItemUids } = data;
        /* istanbul ignore else */ // Always has quote in real usage; covered in integration tests
        if (quote) {
          setQuoteData(quote);
        }
        // Clear selected items that were removed
        /* istanbul ignore else */ // Always has removedItemUids in real usage; covered in integration tests
        if (removedItemUids) {
          setSelectedItemUids((prev) => {
            const next = new Set(prev);
            removedItemUids.forEach((uid) => next.delete(uid));
            return next;
          });
        }
      },
      {
        eager: true,
      }
    );
    return () => quoteItemsRemovedEvent?.off();
  }, []);

  /**
   * Handlers
   */
  const handleItemCheckboxChange = useCallback(
    (item: CartItemModel, isSelected: boolean) => {
      setSelectedItemUids((prev) => {
        const next = new Set(prev);
        if (isSelected) {
          next.add(item.uid);
        } else {
          next.delete(item.uid);
        }
        return next;
      });
    },
    []
  );

  const handleActionsDropdownChange = useCallback(
    (event: Event) => {
      const action = (event.target as HTMLSelectElement).value;
      const currentSelectedUids = selectedItemUidsRef.current;
      /* istanbul ignore else */ if (action === 'remove') {
        setActionsBarDropdownValue(action);
        actionsBarDropdownValueRef.current = action; // Update ref immediately
        /* istanbul ignore next */ if (currentSelectedUids.size === 0) {
          // No items selected - show warning modal
          setIsNoItemsSelectedModalOpen(true);
        } else if (quoteData && removeItemsHandlerRef.current) {
          // Items selected - proceed with removal
          const itemsToRemove = quoteData.items.filter((item) =>
            currentSelectedUids.has(item.uid)
          );
          /* istanbul ignore next */ removeItemsHandlerRef.current(
            itemsToRemove
          );
        }
      }
      onActionsDropdownChange?.(event);
    },
    [onActionsDropdownChange, quoteData]
  );

  /* istanbul ignore next */ const handleCloseNoItemsSelectedModal =
    useCallback(() => {
      setIsNoItemsSelectedModalOpen(false);
      setActionsBarDropdownValue('');
      actionsBarDropdownValueRef.current = '';
    }, []);

  // Listen for quote closed event
  useEffect(() => {
    const quoteClosedEvent = events.on(
      'quote-management/negotiable-quote-closed',
      async (data: { closedQuoteUids: string[] }) => {
        setIsClosing(false);
        setModalErrorMessage('');
        // Show success message in modal
        setModalSuccessMessage(dictionary.closeSuccessMessage);

        // Refresh quote data if the current quote was closed
        // Use a function to get the current quoteData to avoid stale closure
        setQuoteData((currentQuoteData) => {
          if (
            currentQuoteData?.uid &&
            data.closedQuoteUids?.includes(currentQuoteData.uid)
          ) {
            // Trigger async refresh
            getQuoteData(currentQuoteData.uid)
              .then((updatedQuoteData) => {
                setQuoteData(updatedQuoteData);
              })
              .catch((error) => {
                console.warn(
                  'Failed to refresh quote data after closure:',
                  error
                );
              });
          }
          return currentQuoteData; // Return current data unchanged for this setter call
        });

        // Close modal and redirect after a delay to show success message
        setTimeout(() => {
          setShowCloseModal(false);
          setModalSuccessMessage('');
        }, 2000);
      },
      {
        eager: true,
      }
    );
    return () => quoteClosedEvent?.off();
  }, [dictionary.closeSuccessMessage]);

  // Listen for quote close error event
  useEffect(() => {
    const quoteCloseErrorEvent = events.on(
      'quote-management/negotiable-quote-close-error',
      (data: { error: Error; attemptedQuoteUids: string[] }) => {
        console.log('Close error event received:', data);
        setIsClosing(false);
        setModalSuccessMessage('');
        const errorMsg = data.error.message || dictionary.errorFallback;
        setModalErrorMessage(errorMsg);
      },
      {
        eager: true,
      }
    );
    return () => quoteCloseErrorEvent?.off();
  }, [dictionary.errorFallback]);

  // Listen for quote renamed event
  useEffect(() => {
    const quoteRenamedEvent = events.on(
      'quote-management/quote-renamed',
      (data: { quote: NegotiableQuoteModel }) => {
        const { quote } = data;
        setQuoteData(quote);
        setRenameError('');
        setRenameQuoteNameError('');
        setRenameSuccess(dictionary.renameSuccessMessage);

        // Wait 2 seconds before closing the modal
        setTimeout(() => {
          setIsRenameModalOpen(false);
          setRenameQuoteName('');
          setRenameReason('');
          setRenameSuccess('');
        }, 2000);
      },
      {
        eager: true,
      }
    );
    return () => quoteRenamedEvent?.off();
  }, [dictionary.renameSuccessMessage]);

  // Listen for quote duplicated event
  useEffect(() => {
    const quoteDuplicatedEvent = events.on(
      'quote-management/quote-duplicated',
      (data: {
        quote: NegotiableQuoteModel;
        input: { quoteUid: string; duplicatedQuoteUid: string };
      }) => {
        const { quote: newQuote } = data;

        // Call parent callback if provided
        if (quoteData && onDuplicateQuote) {
          onDuplicateQuote({ quoteData, newQuote });
        }

        // Show success in modal
        setDuplicateSuccess(true);
        setDuplicateError(null);

        // Close modal after showing success message
        // Navigation is handled by the application layer listening to quote-duplicated event
        setTimeout(() => {
          setIsDuplicateDialogOpen(false);
          setDuplicateSuccess(false);
          setDuplicateError(null);
        }, 2000);
      },
      {
        eager: true,
      }
    );
    return () => quoteDuplicatedEvent?.off();
  }, [quoteData, onDuplicateQuote]);

  /**
   * File upload handlers
   */

  const handleAttachFiles = useCallback(async (files: File[]) => {
    // Check maxFiles limit
    if (maxFiles && attachedFiles.length + files.length > maxFiles) {
      setFileUploadError(dictionary.maxFilesExceeded.replace('{maxFiles}', String(maxFiles)));
      return;
    }

    // Validate file types and sizes before uploading
    for (const file of files) {
      if (maxFileSize && !validateFileSize(file.size, maxFileSize)) {
        setFileUploadError(
          dictionary.maxFileSizeExceeded.replace('{maxSize}', formatFileSize(maxFileSize))
        );
        return;
      }

      if (acceptedFileTypes && !validateFileType(file.type, acceptedFileTypes)) {
        setFileUploadError(dictionary.invalidFileType);
        return;
      }
    }

    setFileUploadError(undefined);

    // Add files with 'uploading' status
    const newFiles: AttachedFile[] = files.map(file => ({
      key: `temp-${Date.now()}-${Math.random()}-${file.name}`,
      name: file.name,
      size: file.size,
      status: 'uploading',
    }));

    setAttachedFiles(prev => [...prev, ...newFiles]);

    // Upload each file and update its status
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const tempKey = newFiles[i].key;

      try {
        const result = await uploadFile(file);
        setAttachedFiles(prev => prev.map(f =>
          f.key === tempKey
            ? { ...f, key: result.key, status: 'success' }
            : f
        ));
      } catch (error) {
        setAttachedFiles(prev => prev.map(f =>
          f.key === tempKey
            ? { ...f, status: 'error', error: (error as Error).message }
            : f
        ));
      }
    }
  }, [maxFiles, maxFileSize, acceptedFileTypes, attachedFiles, dictionary]);

  const handleRemoveFile = useCallback((key: string) => {
    setAttachedFiles(prev => prev.filter(f => f.key !== key));
  }, []);

  /**
   * VNodes for the ManageNegotiableQuote component
   */

  // Rename handlers
  const handleRenameClick = useCallback(() => {
    setRenameQuoteName(quoteData?.name || '');
    setRenameReason('');
    setRenameError('');
    setRenameQuoteNameError('');
    setRenameSuccess('');
    setIsRenameModalOpen(true);
  }, [quoteData]);

  const handleRenameClose = useCallback(() => {
    setIsRenameModalOpen(false);
    setRenameQuoteName('');
    setRenameReason('');
    setRenameError('');
    setRenameQuoteNameError('');
    setRenameSuccess('');
  }, []);

  const handleRenameQuoteNameChange = useCallback((value: string) => {
    setRenameQuoteName(value);
    if (value.trim()) {
      setRenameQuoteNameError('');
    }
  }, []);

  const handleDuplicateQuote = useCallback(() => {
    // Show confirmation modal
    setIsDuplicateDialogOpen(true);
    setDuplicateError(null);
    setDuplicateSuccess(false);
  }, []);

  const handleRenameSave = useCallback(async () => {
    // Validate quote name
    if (!renameQuoteName.trim()) {
      setRenameQuoteNameError(dictionary.renameQuoteNameRequired);
      return;
    }

    try {
      setRenameError('');
      setRenameQuoteNameError('');
      await renameNegotiableQuote({
        quoteUid: quoteData!.uid,
        quoteName: renameQuoteName,
        quoteComment: renameReason.trim() || undefined,
      });
      // The quote-renamed event will handle closing the modal and updating state
    } catch (error) {
      setRenameError((error as Error).message || dictionary.renameErrorDefault);
    }
  }, [
    quoteData,
    renameQuoteName,
    renameReason,
    dictionary.renameQuoteNameRequired,
    dictionary.renameErrorDefault,
  ]);

  // Quote name with rename icon
  const quoteName = useMemo(() => {
    // Priority 1: Check lockedForEditing if explicitly set
    // Priority 2: Fall back to status check
    const isLocked =
      quoteData &&
      // If lockedForEditing is explicitly set, use it
      (quoteData.lockedForEditing === true ||
        // Otherwise, lock based on status
        (quoteData.lockedForEditing === undefined &&
          (quoteData.status === 'SUBMITTED' ||
            quoteData.status === 'PENDING' ||
            quoteData.status === 'ORDERED' ||
            quoteData.status === 'CLOSED')));

    return (
      <Slot
        name="QuoteName"
        slot={slots?.QuoteName}
        context={{ quoteName: quoteData?.name, quoteData }}
      >
        <div className="quote-management-manage-negotiable-quote__quote-name-wrapper">
          <h1
            data-testid="quote-name"
            className="quote-management-manage-negotiable-quote__quote-name-title"
          >
            {quoteData?.name}
          </h1>
          <button
            onClick={handleRenameClick}
            disabled={isLocked}
            data-testid="rename-quote-button"
            className="quote-management-manage-negotiable-quote__rename-button"
            aria-label="Rename quote"
          >
            <Edit />
          </button>
        </div>
      </Slot>
    );
  }, [slots?.QuoteName, quoteData, handleRenameClick]);

  // Quote status
  const quoteStatus = useMemo(() => {
    return (
      <Slot
        name="QuoteStatus"
        slot={slots?.QuoteStatus}
        context={{ quoteStatus: quoteData?.status, quoteData }}
      >
        <div data-testid="quote-status">{quoteData?.status}</div>
      </Slot>
    );
  }, [slots?.QuoteStatus, quoteData]);

  // Banner
  const getBannerStatusMessage = useCallback(
    (status: string | undefined) => {
      switch (status) {
        case 'SUBMITTED':
          return dictionary.statusSubmitted;
        case 'PENDING':
          return dictionary.statusPending;
        case 'EXPIRED':
          return dictionary.statusExpired;
        default:
          return null;
      }
    },
    [dictionary]
  );

  const bannerMessage = getBannerStatusMessage(quoteData?.status);

  const bannerContent = bannerMessage && (
    <InLineAlert
      type="warning"
      variant="secondary"
      icon={<WarningFilled />}
      heading={dictionary.bannerTitle}
      description={bannerMessage}
    />
  );

  const banner = bannerContent && (
    <Slot
      name="Banner"
      data-testid="banner"
      slot={slots?.Banner}
      context={{ quoteData }}
    >
      {bannerContent}
    </Slot>
  );

  // Handler for dismissing out of stock warning
  const handleDismissOutOfStockWarning = useCallback(() => {
    setDuplicateOutOfStockWarning(false);
  }, []);

  // Out of stock warning banner for duplicated quotes
  const outOfStockWarningBanner = duplicateOutOfStockWarning && (
    <Slot
      name="DuplicateQuoteWarningBanner"
      data-testid="duplicate-outOfStock-warning-banner"
      slot={slots?.DuplicateQuoteWarningBanner}
      context={{ outOfStockWarning: duplicateOutOfStockWarning }}
    >
      <InLineAlert
        type="warning"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.duplicateOutOfStockWarningHeading}
        description={dictionary.duplicateOutOfStockWarningMessage}
        onDismiss={handleDismissOutOfStockWarning}
        data-testid="duplicate-outOfStock-warning-alert"
      />
    </Slot>
  );

  // Details
  const details = useMemo(() => {
    if (!quoteData) {
      return;
    }

    // Some quotes, such as draft quotes, don't have a date formatted expiration date
    const expirationDate = quoteData.expirationDate ? formattedDate(quoteData.expirationDate, 'short') : undefined;

    return (
      <Slot name="Details" slot={slots?.Details} context={{ quoteData }}>
        <div className="quote-management-manage-negotiable-quote__detail">
          <span
            className="quote-management-manage-negotiable-quote__detail-title"
            data-testid="created-label"
          >
            {dictionary.createdLabel}
          </span>
          <span
            className="quote-management-manage-negotiable-quote__detail-content"
            data-testid="created-value"
          >
            {formattedDate(quoteData!.createdAt, 'short')} (
            {quoteData!.buyer.firstname} {quoteData!.buyer.lastname})
          </span>
        </div>
        <div className="quote-management-manage-negotiable-quote__detail">
          <span
            className="quote-management-manage-negotiable-quote__detail-title"
            data-testid="sales-rep-label"
          >
            {dictionary.salesRepLabel}
          </span>
          <span
            className="quote-management-manage-negotiable-quote__detail-content"
            data-testid="sales-rep-value"
          >
            {quoteData!.salesRepName}
          </span>
        </div>
        <div className="quote-management-manage-negotiable-quote__detail">
          <span
            className="quote-management-manage-negotiable-quote__detail-title"
            data-testid="expires-label"
          >
            {dictionary.expiresLabel}
          </span>
          <span
            className="quote-management-manage-negotiable-quote__detail-content"
            data-testid="expires-value"
          >
            {expirationDate}
          </span>
        </div>
      </Slot>
    );
  }, [slots?.Details, dictionary, quoteData]);

  // Action bar
  const buttons: { label: string; value: string; disabled?: boolean }[] =
    useMemo(() => {
      return [
        {
          label: dictionary.close,
          value: 'close',
          disabled: !quoteData?.canClose,
        },
        {
          label: dictionary.delete,
          value: 'delete',
          disabled: !quoteData?.canDelete,
        },
        {
          label: dictionary.print,
          value: 'print',
        },
        {
          label: dictionary.createTemplate,
          value: 'createTemplate',
          disabled: Boolean(quoteData?.templateName),
        },
        {
          label: dictionary.createCopy,
          value: 'createCopy',
          disabled: Boolean(quoteData?.templateName),
        },
      ];
    }, [dictionary, quoteData?.canDelete, quoteData?.canClose, quoteData?.templateName]);
  const actionBar = useMemo(() => {
    const handleActionsButtonClick = (action: string) => {
      switch (action) {
        case 'close':
          setShowCloseModal(true);
          setModalErrorMessage(''); // Clear modal errors
          setModalSuccessMessage(''); // Clear modal success messages
          onActionsButtonClick?.(action);
          break;
        case 'delete':
          setIsDeleteDialogOpen(true);
          onActionsButtonClick?.(action);
          break;
        case 'print':
          onActionsButtonClick?.(action);
          break;
        case 'createTemplate':
          setShowCreateTemplateModal(true);
          setCreateTemplateErrorMessage(''); // Clear modal errors
          setCreateTemplateSuccessMessage(''); // Clear modal success messages
          onActionsButtonClick?.(action);
          break;
        case 'createCopy':
          handleDuplicateQuote();
          onActionsButtonClick?.(action);
          break;
      }
    };
    return (
      <Slot
        name="ActionBar"
        slot={slots?.ActionBar}
        context={{ quoteData, actionsBarDropdownValue }}
      >
        <ActionsBar
          dropdownPlaceholder={dictionary.actionsLabel}
          dropdownOptions={[
            {
              label: dictionary.remove,
              value: 'remove',
            },
          ]}
          dropdownValue={actionsBarDropdownValue}
          handleDropdownChange={handleActionsDropdownChange}
          buttons={buttons.map((button) => (
            <Button
              key={button.value}
              variant="tertiary"
              onClick={() => handleActionsButtonClick(button.value)}
              data-testid={`actions-bar-${button.value}-button`}
              data-action={button.value}
              disabled={button.disabled}
            >
              {button.label}
            </Button>
          ))}
        />
      </Slot>
    );
  }, [
    buttons,
    dictionary,
    slots?.ActionBar,
    quoteData,
    handleActionsDropdownChange,
    onActionsButtonClick,
    handleDuplicateQuote,
    actionsBarDropdownValue,
  ]);

  // Quote content
  const quoteContent = useMemo(() => {
    return (
      <Slot
        name="QuoteContent"
        slot={slots?.QuoteContent}
        context={{ quoteData }}
      >
        <TabbedContent
          tabs={
            new Map([
              ['items-quoted', 'Items Quoted'],
              ['comments', 'Comments'],
              ['history-log', 'History Log'],
            ])
          }
          tabsContent={
            new Map([
              [
                'items-quoted',
                <Slot
                  key="items-quoted"
                  name="ItemsQuotedTab"
                  slot={slots?.ItemsQuotedTab}
                  context={{ quoteData }}
                >
                  <ItemsQuoted
                    quoteData={quoteData}
                    onItemCheckboxChange={handleItemCheckboxChange}
                    onRemoveItemsRef={(handler) => {
                      removeItemsHandlerRef.current = handler;
                    }}
                    onRemoveModalStateChange={
                      /* istanbul ignore next */ (isOpen) => {
                        if (
                          !isOpen &&
                          actionsBarDropdownValueRef.current === 'remove'
                        ) {
                          setActionsBarDropdownValue('');
                        }
                      }
                    }
                  />
                </Slot>,
              ],
              [
                'comments',
                <Slot
                  key="comments"
                  name="CommentsTab"
                  slot={slots?.CommentsTab}
                  context={{ quoteData }}
                >
                  <QuoteCommentsList quoteData={quoteData} />
                </Slot>,
              ],
              [
                'history-log',
                <Slot
                  key="history-log"
                  name="HistoryLogTab"
                  slot={slots?.HistoryLogTab}
                  context={{ quoteData }}
                >
                  <QuoteHistoryLog quoteData={quoteData} />
                </Slot>,
              ],
            ])
          }
        />
      </Slot>
    );
  }, [
    slots?.QuoteContent,
    slots?.HistoryLogTab,
    slots?.CommentsTab,
    slots?.ItemsQuotedTab,
    quoteData,
    handleItemCheckboxChange,
  ]);

  // Shipping information title
  const shippingInformationTitle = useMemo(() => {
    return (
      <Slot
        name="ShippingInformationTitle"
        slot={slots?.ShippingInformationTitle}
        context={{ quoteData }}
      >
        <h2>{dictionary.shippingInformationTitle}</h2>
      </Slot>
    );
  }, [slots?.ShippingInformationTitle, quoteData, dictionary]);

  // Shipping information
  const shippingInformation = useMemo(() => {
    return (
      <Slot
        name="ShippingInformation"
        slot={slots?.ShippingInformation}
        context={{ quoteData, loading, setLoading }}
      >
        <ShippingAddressDisplay
          shippingAddress={quoteData?.shippingAddresses?.[0]}
          loading={loading}
          data-testid="manage-quote-shipping-address"
        />
      </Slot>
    );
  }, [slots?.ShippingInformation, quoteData, loading, setLoading]);

  // Quote comments title
  const quoteCommentsTitle = useMemo(() => {
    return (
      <Slot
        name="QuoteCommentsTitle"
        slot={slots?.QuoteCommentsTitle}
        context={{ quoteData }}
      >
        <h2>{dictionary.quoteCommentsTitle}</h2>
      </Slot>
    );
  }, [slots?.QuoteCommentsTitle, dictionary, quoteData]);

  // Quote comments
  const quoteComments = useMemo(() => {
    return (
      <Slot
        name="QuoteComments"
        slot={slots?.QuoteComments}
        context={{ quoteData }}
      >
        <TextArea
          name="quoteComments"
          placeholder={dictionary.quoteCommentsPlaceholder}
          rows={3}
          value={comment}
          onInput={(e) => {
            setComment((e.target as HTMLTextAreaElement).value);
          }}
          disabled={!quoteData?.canSendForReview || isSubmitting}
        />
      </Slot>
    );
  }, [slots?.QuoteComments, dictionary, quoteData, comment, isSubmitting]);

  // File upload should be disabled when quote cannot be sent for review
  // (which already includes the status and permission checks from the transformer)
  const isFileUploadDisabled = useMemo(() => {
    return !quoteData?.canSendForReview;
  }, [quoteData]);

  // File upload input
  const attachFilesField = useMemo(() => {
    // Check if button should be visually disabled
    const isDisabled = isSubmitting || isFileUploadDisabled;

    return (
      <Slot
        name="AttachFilesField"
        slot={slots?.AttachFilesField}
        context={{ onFileChange: handleAttachFiles, attachedFiles, fileUploadError, disabled: isDisabled }}
      >
        <div 
          className="quote-management-manage-negotiable-quote__attach-files"
          {...(isDisabled ? { 'data-disabled': true } : {})}
          aria-disabled={isDisabled}
        >
          <InputFile
            id="manage-quote-attach-files"
            multiple
            onChange={(e: Event) => {
              const target = e.target as HTMLInputElement;
              const fileList = target?.files;
              const files = fileList ? Array.from(fileList) : [];
              if (files.length > 0) {
                void handleAttachFiles(files);
              }
            }}
            icon={<Add />}
            disabled={isDisabled}
            data-testid="manage-quote-attach-files"
            accept={acceptedFileTypes?.join(',')}
          >
            {dictionary.attachFiles}
          </InputFile>
          {fileUploadError && (
            <div
              className="quote-management-manage-negotiable-quote__file-error"
              data-testid="file-upload-error"
            >
              {fileUploadError}
            </div>
          )}
        </div>
      </Slot>
    );
  }, [
    slots?.AttachFilesField,
    handleAttachFiles,
    isSubmitting,
    isFileUploadDisabled,
    acceptedFileTypes,
    fileUploadError,
    dictionary,
    attachedFiles,
  ]);

  // Attached files list
  const attachedFilesList = useMemo(() => {
    if (!attachedFiles.length) return undefined;

    return (
      <Slot
        name="AttachedFilesList"
        slot={slots?.AttachedFilesList}
        context={{ files: attachedFiles, onRemove: handleRemoveFile, disabled: isSubmitting }}
      >
        <AttachedFilesList
          files={attachedFiles}
          onRemove={handleRemoveFile}
          disabled={isSubmitting}
        />
      </Slot>
    );
  }, [attachedFiles, handleRemoveFile, isSubmitting, slots?.AttachedFilesList]);

  // Close quote handler - only called when quoteData exists
  const handleCloseQuote = useCallback(async () => {
    if (!quoteData?.uid) {
      return;
    }
    setIsClosing(true);
    setModalErrorMessage('');
    setModalSuccessMessage('');
    try {
      await closeNegotiableQuote({
        quoteUids: [quoteData!.uid],
      });
    } catch (error) {
      console.error('Failed to close quote:', error);
    }
  }, [quoteData]);

  const handleCloseModalCancel = useCallback(() => {
    setShowCloseModal(false);
    setModalErrorMessage('');
    setModalSuccessMessage('');
  }, []);

  // Create template handler
  const handleCreateTemplate = useCallback(async () => {
    if (!quoteData?.uid) {
      return;
    }
    setIsCreatingTemplate(true);
    setCreateTemplateErrorMessage('');
    setCreateTemplateSuccessMessage('');
    try {
      await createQuoteTemplate(quoteData.uid);
      // Show success message
      setCreateTemplateSuccessMessage(dictionary.createTemplateSuccessMessage);
       // Close modal after a delay to show success message
       setTimeout(() => {
        setShowCreateTemplateModal(false);
        setCreateTemplateSuccessMessage('');
      }, 2000);
    } catch (error) {
      console.error('Failed to create quote template:', error);
      const errorMessage =
        error instanceof Error ? error.message : dictionary.createTemplateErrorFallback;
      setCreateTemplateErrorMessage(errorMessage);
    } finally {
      setIsCreatingTemplate(false);
    }
  }, [quoteData, dictionary.createTemplateSuccessMessage, dictionary.createTemplateErrorFallback]);

  const handleCreateTemplateModalCancel = useCallback(() => {
    setShowCreateTemplateModal(false);
    setCreateTemplateErrorMessage('');
    setCreateTemplateSuccessMessage('');
  }, []);

  // Footer
  const handleSendForReview = useCallback(async () => {
    const successfulAttachments = attachedFiles.filter(f => f.status === 'success');

    // If override prop is provided, use it instead of the API
    if (onSendForReview) {
      onSendForReview({
        quoteData: quoteData!,
        comment: comment.trim() || undefined,
        attachments: successfulAttachments.length ?
          successfulAttachments :
          undefined,
      });
      return;
    }

    // Otherwise, use the sendForReview API
    setIsSubmitting(true);
    try {
      await sendForReview({
        quoteUid: quoteData!.uid,
        comment: comment.trim() || undefined,
        attachments: successfulAttachments.length
          ? successfulAttachments.map(f => ({ key: f.key }))
          : undefined,
      });
      // The quote-sent-for-review event will handle updating state
    } catch (error) {
      console.error('Failed to send quote for review:', error);
      setIsSubmitting(false);
      // TODO: Show error notification to user
    }
  }, [quoteData, onSendForReview, comment, attachedFiles]);

  const footer = useMemo(() => {
    return (
      <Slot
        name="Footer"
        slot={slots?.Footer}
        context={{ quoteData, comment, isSubmitting, attachments: attachedFiles, handleSendForReview }}
      >
        <Button
          variant="primary"
          onClick={handleSendForReview}
          data-testid="send-for-review-button"
          disabled={!quoteData?.canSendForReview || isSubmitting}
        >
          {dictionary.sendForReview}
        </Button>
      </Slot>
    );
  }, [
    slots?.Footer,
    dictionary,
    quoteData,
    comment,
    isSubmitting,
    attachedFiles,
    handleSendForReview,
  ]);

  // Rename modal error banner
  const renameErrorBanner = useMemo(
    () =>
      renameError ? (
        <InLineAlert
          type="error"
          variant="primary"
          icon={<WarningFilled color="var(--color-error)" />}
          heading={dictionary.renameErrorHeading}
          description={renameError}
        />
      ) : undefined,
    [renameError, dictionary.renameErrorHeading]
  );

  // Rename modal success banner
  const renameSuccessBanner = useMemo(
    () =>
      renameSuccess ? (
        <InLineAlert
          type="success"
          variant="primary"
          icon={<CheckWithCircle />}
          heading={dictionary.renameSuccessHeading}
          description={renameSuccess}
        />
      ) : undefined,
    [renameSuccess, dictionary.renameSuccessHeading]
  );

  // Delete quote functionality
  const getDeleteErrorMessage = useCallback(
    (error: any): string => {
      // If it's an operation failure with errors array, get the first error message
      if (
        error.errors &&
        Array.isArray(error.errors) &&
        error.errors.length > 0
      ) {
        return (
          error.errors[0].message ||
          dictionary.confirmationModalDeleteErrorFallback
        );
      }
      // Otherwise use the error message or fallback
      return error?.message || dictionary.confirmationModalDeleteErrorFallback;
    },
    [dictionary.confirmationModalDeleteErrorFallback]
  );

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDeleteError(null);
    setDeleteSuccess(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!quoteData?.uid) {
      return;
    }

    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      const { operationResults } = await deleteQuote(quoteData.uid);

      const failure = operationResults.find(
        (res: any) => res.__typename === 'DeleteNegotiableQuoteOperationFailure'
      ) as any;
      if (failure) {
        const errorMessage = getDeleteErrorMessage(failure);
        setDeleteError(errorMessage);
        return;
      }

      setDeleteSuccess(true);
    } catch (error: any) {
      const errorMessage = getDeleteErrorMessage(error);
      setDeleteError(errorMessage);
    }
  }, [quoteData, getDeleteErrorMessage]);

  const handleDismissError = useCallback(() => {
    setDeleteError(null);
  }, []);

  const handleDismissSuccess = useCallback(() => {
    setDeleteSuccess(false);
  }, []);

  // Duplicate quote functionality
  const getDuplicateErrorMessage = useCallback((error: any): string => {
    return error?.message || dictionary.confirmationModalDuplicateErrorFallback;
  }, [dictionary.confirmationModalDuplicateErrorFallback]);

  const handleDuplicateCancel = useCallback(() => {
    setIsDuplicateDialogOpen(false);
    setDuplicateError(null);
    setDuplicateSuccess(false);
  }, []);

  const handleDuplicateConfirm = useCallback(async () => {
    if (!quoteData?.uid) {
      return;
    }

    setDuplicateError(null);
    setDuplicateSuccess(false);

    try {
      // Check for out of stock items before duplication
      /* istanbul ignore next */
      const outOfStockItems = quoteData.items?.filter((item) => item.outOfStock) || [];

      // Generate a unique duplicated quote UID by replacing the last 8 characters
      const generateUniqueUid = (originalUid: string): string => {
        const baseUid = originalUid.slice(0, -8); // Remove last 8 characters
        const randomSuffix = crypto.randomUUID().slice(0, 8);
        return baseUid + randomSuffix;
      };
      
      const duplicatedQuoteUid = generateUniqueUid(quoteData.uid);

      // Store out of stock warning for the new quote if any items are out of stock
      const hasOutOfStockItems = outOfStockItems.length > 0;
      if (hasOutOfStockItems) {
        sessionStorage.setItem(`duplicate-outOfStock-warning-${duplicatedQuoteUid}`, 'true');
      }

      await duplicateQuote({ 
        quoteUid: quoteData.uid, 
        duplicatedQuoteUid,
        hasOutOfStockItems 
      });
      // Success handling is done in the event listener
    } catch (error: any) {
      const errorMessage = getDuplicateErrorMessage(error);
      setDuplicateError(errorMessage);
    }
  }, [quoteData, getDuplicateErrorMessage]);

  const handleDismissDuplicateError = useCallback(() => {
    setDuplicateError(null);
  }, []);

  const handleDismissDuplicateSuccess = useCallback(() => {
    setDuplicateSuccess(false);
  }, []);

  const deleteErrorBanner = deleteError ? (
    <InLineAlert
      type="error"
      variant="primary"
      icon={<WarningFilled />}
      heading={dictionary.confirmationModalDeleteErrorHeading}
      description={deleteError}
      onDismiss={handleDismissError}
      data-testid="confirmation-modal-error-banner"
    />
  ) : null;

  const deleteSuccessBanner = deleteSuccess ? (
    <InLineAlert
      type="success"
      variant="primary"
      icon={<CheckWithCircle />}
      heading={dictionary.confirmationModalDeleteSuccessHeading}
      description={dictionary.confirmationModalDeleteSuccessDescription}
      onDismiss={handleDismissSuccess}
      data-testid="confirmation-modal-success-banner"
    />
  ) : null;

  const deleteBanner = deleteSuccessBanner || deleteErrorBanner;

  const duplicateErrorBanner = duplicateError ? (
    <InLineAlert
      type="error"
      variant="primary"
      icon={<WarningFilled />}
      heading={dictionary.confirmationModalDuplicateErrorHeading}
      description={duplicateError}
      onDismiss={handleDismissDuplicateError}
      data-testid="confirmation-modal-duplicate-error-banner"
    />
  ) : null;

  const duplicateSuccessBanner = duplicateSuccess ? (
    <InLineAlert
      type="success"
      variant="primary"
      icon={<CheckWithCircle />}
      heading={dictionary.confirmationModalDuplicateSuccessHeading}
      description={dictionary.confirmationModalDuplicateSuccessDescription}
      onDismiss={handleDismissDuplicateSuccess}
      data-testid="confirmation-modal-duplicate-success-banner"
    />
  ) : null;

  const duplicateBanner = duplicateSuccessBanner || duplicateErrorBanner;

  const manageNegotiableQuoteProps: ManageNegotiableQuoteComponentProps = {
    ...props,
    loading,
    quoteName,
    quoteStatus,
    banner: (outOfStockWarningBanner || banner) ? (<>{outOfStockWarningBanner}{banner}</>) : undefined,
    details,
    actionBar,
    quoteContent,
    shippingInformationTitle,
    shippingInformation,
    quoteCommentsTitle,
    quoteComments,
    attachFilesField,
    attachedFilesList,
    footer,
  };

  // Create banner for close modal based on success/error state
  let closeModalBanner = null;
  if (modalSuccessMessage) {
    closeModalBanner = (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<CheckWithCircle />}
        heading={dictionary.closeModalSuccessHeading}
        description={modalSuccessMessage}
        data-testid="close-quote-success-banner"
      />
    );
  } else if (modalErrorMessage) {
    closeModalBanner = (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.confirmationModalDeleteErrorHeading}
        description={modalErrorMessage}
        data-testid="close-quote-error-banner"
      />
    );
  }

  // Create banner for create template modal based on success/error state
  let createTemplateModalBanner = null;
  if (createTemplateSuccessMessage) {
    createTemplateModalBanner = (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<CheckWithCircle />}
        heading={dictionary.createTemplateModalSuccessHeading}
        description={createTemplateSuccessMessage}
        data-testid="create-template-success-banner"
      />
    );
  } else if (createTemplateErrorMessage) {
    createTemplateModalBanner = (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.createTemplateModalErrorHeading}
        description={createTemplateErrorMessage}
        data-testid="create-template-error-banner"
      />
    );
  }

  return (
    <>
      <ManageNegotiableQuoteComponent {...manageNegotiableQuoteProps} />
      {isRenameModalOpen &&
        createPortal(
          <RenameQuoteModal
            open={isRenameModalOpen}
            quoteName={renameQuoteName}
            renameReason={renameReason}
            quoteNameError={renameQuoteNameError}
            errorBanner={renameErrorBanner}
            successBanner={renameSuccessBanner}
            onQuoteNameChange={handleRenameQuoteNameChange}
            onRenameReasonChange={setRenameReason}
            onSave={handleRenameSave}
            onClose={handleRenameClose}
          />,
          document.body
        )}
      {isDeleteDialogOpen &&
        createPortal(
          <ConfirmationModal
            open={isDeleteDialogOpen}
            title={dictionary.confirmationModalDeleteTitle}
            message={dictionary.confirmationModalDeleteMessage}
            cancelLabel={dictionary.confirmationModalCancel}
            confirmLabel={dictionary.confirmationModalDeleteConfirm}
            onCancel={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            showCloseButton={!deleteBanner}
            confirmationBanner={deleteBanner}
          />,
          document.body
        )}
      {isDuplicateDialogOpen &&
        createPortal(
          <ConfirmationModal
            open={isDuplicateDialogOpen}
            title={dictionary.confirmationModalDuplicateTitle}
            message={dictionary.confirmationModalDuplicateMessage}
            cancelLabel={dictionary.confirmationModalCancel}
            confirmLabel={dictionary.confirmationModalDuplicateConfirm}
            onCancel={handleDuplicateCancel}
            onConfirm={handleDuplicateConfirm}
            showCloseButton={!duplicateBanner}
            confirmationBanner={duplicateBanner}
          />,
          document.body
        )}
      {showCloseModal &&
        createPortal(
          <ConfirmationModal
            open={showCloseModal}
            title={dictionary.closeModalTitle}
            message={dictionary.closeModalMessage}
            cancelLabel={dictionary.closeModalCancel}
            confirmLabel={
              isClosing
                ? dictionary.closeModalConfirmLoading
                : dictionary.closeModalConfirm
            }
            onCancel={handleCloseModalCancel}
            onConfirm={isClosing ? undefined : handleCloseQuote}
            showCloseButton={true}
            confirmationBanner={closeModalBanner}
            data-testid="close-quote-confirmation-modal"
          />,
          document.body
        )}
      {showCreateTemplateModal &&
        createPortal(
          <ConfirmationModal
            open={showCreateTemplateModal}
            title={dictionary.createTemplateModalTitle}
            message={dictionary.createTemplateModalMessage}
            cancelLabel={dictionary.createTemplateModalCancel}
            confirmLabel={
              isCreatingTemplate
                ? dictionary.createTemplateModalConfirmLoading
                : dictionary.createTemplateModalConfirm
            }
            onCancel={handleCreateTemplateModalCancel}
            onConfirm={isCreatingTemplate ? undefined : handleCreateTemplate}
            showCloseButton={true}
            confirmationBanner={createTemplateModalBanner}
            data-testid="create-template-confirmation-modal"
          />,
          document.body
        )}
      {isNoItemsSelectedModalOpen &&
        createPortal(
          <ConfirmationModal
            open={isNoItemsSelectedModalOpen}
            title={dictionary.noItemsSelectedModalTitle}
            message={dictionary.noItemsSelectedModalMessage}
            confirmLabel={dictionary.noItemsSelectedModalConfirm}
            onConfirm={handleCloseNoItemsSelectedModal}
            onClose={handleCloseNoItemsSelectedModal}
            showCloseButton={true}
            data-testid="no-items-selected-modal"
          />,
          document.body
        )}
    </>
  );
};
